import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material'
import {
  Visibility,
  Code,
  Refresh,
  Help,
  Download,
  ContentCopy,
  Clear,
  Save,
  FolderOpen,
  Delete,
  Edit
} from '@mui/icons-material'
import axios from 'axios'
import mermaid from 'mermaid'

// Inicializar Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif'
})

const DiagramBuilder = () => {
  const [mermaidCode, setMermaidCode] = useState(`flowchart TD
    A[Inicio] --> B[Proceso]
    B --> C{Decisión}
    C -->|Sí| D[Acción 1]
    C -->|No| E[Acción 2]
    D --> F[Fin]
    E --> F`)
  const [svgContent, setSvgContent] = useState('')
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [success, setSuccess] = useState('')
  const [savedDiagrams, setSavedDiagrams] = useState([])
  const [currentDiagramId, setCurrentDiagramId] = useState(null)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [loadDialogOpen, setLoadDialogOpen] = useState(false)
  const [diagramName, setDiagramName] = useState('')
  const [diagramDescription, setDiagramDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const diagramRef = useRef(null)

  useEffect(() => {
    if (mermaidCode) {
      renderDiagram()
    }
    loadSavedDiagrams()
  }, [])

  const loadSavedDiagrams = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/diagramas-guardados', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSavedDiagrams(response.data)
    } catch (error) {
      console.error('Error al cargar diagramas guardados:', error)
    }
  }

  const handleOpenSaveDialog = () => {
    if (currentDiagramId) {
      // Si estamos editando un diagrama existente, actualizar directamente
      handleUpdateDiagram()
    } else {
      // Si es nuevo, abrir diálogo
      setSaveDialogOpen(true)
    }
  }

  const handleSaveDiagram = async () => {
    if (!diagramName.trim()) {
      setError('El nombre del diagrama es obligatorio')
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')

      const diagramData = {
        nombre: diagramName,
        descripcion: diagramDescription,
        codigo_mermaid: mermaidCode,
        svg_content: svgContent
      }

      await axios.post('/api/diagramas-guardados', diagramData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Diagrama guardado exitosamente')
      setSaveDialogOpen(false)
      setDiagramName('')
      setDiagramDescription('')
      loadSavedDiagrams()
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateDiagram = async () => {
    if (!currentDiagramId) return

    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')

      const diagramData = {
        codigo_mermaid: mermaidCode,
        svg_content: svgContent
      }

      await axios.put(`/api/diagramas-guardados/${currentDiagramId}`, diagramData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Diagrama actualizado exitosamente')
      loadSavedDiagrams()
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      setError('Error al actualizar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleLoadDiagram = (diagram) => {
    setMermaidCode(diagram.codigo_mermaid)
    setSvgContent(diagram.svg_content || '')
    setCurrentDiagramId(diagram.id)
    setDiagramName(diagram.nombre)
    setDiagramDescription(diagram.descripcion || '')
    setLoadDialogOpen(false)
    setSuccess(`Diagrama "${diagram.nombre}" cargado`)
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleDeleteDiagram = async (diagramId) => {
    if (!window.confirm('¿Está seguro de eliminar este diagrama?')) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/diagramas-guardados/${diagramId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Diagrama eliminado exitosamente')
      loadSavedDiagrams()

      // Si estamos editando el diagrama que se eliminó, crear uno nuevo
      if (currentDiagramId === diagramId) {
        handleNewDiagram()
      }

      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleNewDiagram = () => {
    setMermaidCode(`flowchart TD
    A[Inicio] --> B[Proceso]
    B --> C{Decisión}
    C -->|Sí| D[Acción 1]
    C -->|No| E[Acción 2]
    D --> F[Fin]
    E --> F`)
    setSvgContent('')
    setCurrentDiagramId(null)
    setDiagramName('')
    setDiagramDescription('')
    setSuccess('Nuevo diagrama creado')
    setTimeout(() => setSuccess(''), 3000)
  }

  const renderDiagram = async () => {
    try {
      setError('')

      if (!mermaidCode.trim()) {
        setError('El código Mermaid no puede estar vacío')
        return
      }

      const elementId = `mermaid-diagram-${Date.now()}`

      const { svg } = await mermaid.render(elementId, mermaidCode)
      setSvgContent(svg)
    } catch (err) {
      console.error('Error al renderizar diagrama:', err)
      setError(`Error al renderizar el diagrama: ${err.message}`)
    }
  }

  const handleCodeChange = (e) => {
    setMermaidCode(e.target.value)
    setError('')
  }

  const handleClear = () => {
    setMermaidCode('')
    setSvgContent('')
    setError('')
    setSuccess('')
  }

  const handleCopyCode = () => {
    navigator.clipboard.writeText(mermaidCode)
    setSuccess('Código copiado al portapapeles')
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleDownloadSVG = () => {
    if (!svgContent) {
      setError('Primero debe renderizar un diagrama')
      return
    }

    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `diagrama_${Date.now()}.svg`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    setSuccess('Diagrama descargado exitosamente')
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleDownloadPNG = async () => {
    if (!svgContent) {
      setError('Primero debe renderizar un diagrama')
      return
    }

    try {
      // Crear un elemento temporal para obtener las dimensiones del SVG
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = svgContent
      const svgElement = tempDiv.querySelector('svg')

      if (!svgElement) {
        setError('No se pudo procesar el SVG')
        return
      }

      // Obtener dimensiones del SVG
      const bbox = svgElement.getBBox ? svgElement.getBBox() : { width: 800, height: 600 }
      const width = svgElement.viewBox?.baseVal?.width || bbox.width || 800
      const height = svgElement.viewBox?.baseVal?.height || bbox.height || 600

      // Crear canvas con las dimensiones del SVG
      const canvas = document.createElement('canvas')
      const scale = 2 // Factor de escala para mejor calidad
      canvas.width = width * scale
      canvas.height = height * scale
      const ctx = canvas.getContext('2d')

      // Configurar el contexto para mejor calidad
      ctx.scale(scale, scale)
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, width, height)

      const img = new Image()

      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob((blob) => {
            if (!blob) {
              setError('Error al generar la imagen PNG')
              return
            }

            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `diagrama_${Date.now()}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            setSuccess('Diagrama descargado como PNG exitosamente')
            setTimeout(() => setSuccess(''), 3000)
          }, 'image/png')
        } catch (drawError) {
          setError(`Error al dibujar en canvas: ${drawError.message}`)
        }
      }

      img.onerror = (err) => {
        console.error('Error al cargar imagen:', err)
        setError('Error al cargar el SVG para conversión. Intente descargar como SVG en su lugar.')
      }

      // Convertir SVG a data URL en base64 para evitar problemas de CORS
      const svgString = new XMLSerializer().serializeToString(svgElement)
      const svgBase64 = btoa(unescape(encodeURIComponent(svgString)))
      const dataUrl = `data:image/svg+xml;base64,${svgBase64}`

      img.src = dataUrl
    } catch (err) {
      console.error('Error al descargar PNG:', err)
      setError(`Error al descargar PNG: ${err.message}`)
    }
  }

  const exampleTemplates = [
    {
      name: 'Diagrama de Flujo Básico',
      code: `flowchart TD
    A[Inicio] --> B[Proceso]
    B --> C{Decisión}
    C -->|Sí| D[Acción 1]
    C -->|No| E[Acción 2]
    D --> F[Fin]
    E --> F`
    },
    {
      name: 'Proceso con Subprocesos',
      code: `flowchart TD
    A[Inicio] --> B[Análisis]
    B --> C{¿Aprobado?}
    C -->|Sí| D[Implementación]
    C -->|No| E[Revisión]
    E --> B
    D --> F[Pruebas]
    F --> G{¿Exitoso?}
    G -->|Sí| H[Despliegue]
    G -->|No| D
    H --> I[Fin]`
    },
    {
      name: 'Proceso Administrativo',
      code: `flowchart TD
    A[Solicitud Recibida] --> B[Verificar Documentos]
    B --> C{¿Completos?}
    C -->|No| D[Solicitar Documentos Faltantes]
    D --> B
    C -->|Sí| E[Análisis de Requisitos]
    E --> F{¿Cumple Requisitos?}
    F -->|No| G[Notificar Rechazo]
    F -->|Sí| H[Aprobar Solicitud]
    H --> I[Generar Documento]
    I --> J[Notificar Aprobación]
    G --> K[Fin]
    J --> K`
    }
  ]

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
            Constructor de Diagramas de Flujo
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Herramienta para crear diagramas de flujo usando sintaxis Mermaid
          </Typography>
          {currentDiagramId && (
            <Chip
              label={`Editando: ${diagramName}`}
              color="primary"
              size="small"
              sx={{ mt: 1 }}
              onDelete={handleNewDiagram}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FolderOpen />}
            onClick={() => setLoadDialogOpen(true)}
          >
            Cargar
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleOpenSaveDialog}
          >
            {currentDiagramId ? 'Actualizar' : 'Guardar'}
          </Button>
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab icon={<Code />} label="Editor de Código" />
          <Tab icon={<Visibility />} label="Vista Previa" />
        </Tabs>
      </Paper>

      <Grid container spacing={3}>
        {/* Panel Izquierdo - Editor */}
        <Grid item xs={12} md={activeTab === 0 ? 12 : 6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Código Mermaid</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Limpiar">
                    <IconButton size="small" onClick={handleClear} color="error">
                      <Clear />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar código">
                    <IconButton size="small" onClick={handleCopyCode}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ayuda">
                    <IconButton size="small" onClick={() => setHelpModalOpen(true)}>
                      <Help />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={20}
                value={mermaidCode}
                onChange={handleCodeChange}
                placeholder="Ingrese el código Mermaid aquí..."
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }
                }}
              />

              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={renderDiagram}
                fullWidth
                size="large"
              >
                Renderizar Diagrama
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel Derecho - Vista Previa */}
        {activeTab === 1 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Vista Previa</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Descargar SVG">
                      <IconButton size="small" onClick={handleDownloadSVG}>
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDownloadPNG}
                    >
                      PNG
                    </Button>
                  </Box>
                </Box>

                <Box
                  ref={diagramRef}
                  sx={{
                    minHeight: 400,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: '#fafafa',
                    overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: svgContent }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Plantillas de Ejemplo */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plantillas de Ejemplo
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {exampleTemplates.map((template, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          borderColor: 'primary.main'
                        }
                      }}
                      onClick={() => {
                        setMermaidCode(template.code)
                        setActiveTab(0)
                        setSuccess(`Plantilla "${template.name}" cargada`)
                        setTimeout(() => setSuccess(''), 3000)
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click para cargar esta plantilla
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de Ayuda */}
      <Dialog open={helpModalOpen} onClose={() => setHelpModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ayuda - Sintaxis Mermaid</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Sintaxis Básica de Diagramas de Flujo
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            1. Tipos de Nodos:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }}>
{`A[Rectángulo] - Proceso
B(Rectángulo redondeado) - Inicio/Fin
C{Diamante} - Decisión
D[(Base de datos)]
E((Círculo)) - Evento`}
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            2. Conexiones:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }}>
{`A --> B - Flecha
A --- B - Línea sin flecha
A -->|Texto| B - Flecha con etiqueta
A -.-> B - Flecha punteada`}
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            3. Ejemplo Completo:
          </Typography>
          <Box component="pre" sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto' }}>
{`flowchart TD
    A[Inicio] --> B[Proceso]
    B --> C{¿Condición?}
    C -->|Sí| D[Acción 1]
    C -->|No| E[Acción 2]
    D --> F[Fin]
    E --> F`}
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Para más información, visite la documentación oficial de Mermaid:
            <br />
            <a href="https://mermaid.js.org/syntax/flowchart.html" target="_blank" rel="noopener noreferrer">
              https://mermaid.js.org/syntax/flowchart.html
            </a>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModalOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Guardar Diagrama */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Guardar Diagrama</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nombre del diagrama"
              value={diagramName}
              onChange={(e) => setDiagramName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Descripción (opcional)"
              value={diagramDescription}
              onChange={(e) => setDiagramDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveDiagram} variant="contained" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Cargar Diagrama */}
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Mis Diagramas Guardados</DialogTitle>
        <DialogContent>
          {savedDiagrams.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No tiene diagramas guardados. Cree y guarde un diagrama para verlo aquí.
            </Alert>
          ) : (
            <List>
              {savedDiagrams.map((diagram) => (
                <ListItem
                  key={diagram.id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <ListItemText
                    primary={diagram.nombre}
                    secondary={
                      <>
                        {diagram.descripcion && (
                          <Typography variant="body2" color="text.secondary">
                            {diagram.descripcion}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Actualizado: {new Date(diagram.updated_at).toLocaleString('es-ES')}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Cargar diagrama">
                      <IconButton
                        edge="end"
                        onClick={() => handleLoadDiagram(diagram)}
                        color="primary"
                      >
                        <FolderOpen />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteDiagram(diagram.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DiagramBuilder
